import axios from 'axios';
import {
  addActingOrganizationHeader,
  exposeValidationErrors,
  freshAccessToken,
  handleOrganizationError,
  reportMaintenance,
} from '@koob/utils';
import { camelizeKeys } from 'humps';
import { refreshToken, revokeToken } from '../auth';
import { API_NODE_BASE } from '../../constants';

const formatConfig = ({ params, ...opts } = {}) => ({
  ...opts,
});

const impersonationToken = localStorage.getItem('impersonationToken');
const impersonating = impersonationToken?.length > 0;

// The API's session-expiry 401 body was plain text ("Unauthorized") on older
// builds and is now a JSON envelope with a stable code; accept both during
// deploy skew.
const isAuthExpired = (data) =>
  typeof data === 'string'
    ? data.includes('Unauthorized')
    : data?.code === 'AUTHENTICATION_REQUIRED';

const setupInterceptors = (instance) => {
  instance.interceptors.request.use((config) => {
    const accessToken = impersonating
      ? impersonationToken
      : localStorage.getItem('accessToken');

    config.headers['Accept'] = 'application/json';

    if (Boolean(accessToken)) {
      config.headers['Authorization'] = `Bearer ${accessToken}`;
    }

    return addActingOrganizationHeader(config);
  });

  instance.interceptors.response.use(
    (response) => response,
    async (error) => {
      reportMaintenance(error);
      // Before the session logic: a refused X-Organization-Id is not an
      // expired session (KD-3735).
      if (handleOrganizationError(error)) return Promise.reject(error);
      // KD-3907: an expired access token is refreshed first; only a session
      // that cannot be refreshed logs out.
      if (error.response?.status === 401 && !impersonating) {
        const token = await freshAccessToken(error.config, refreshToken);
        if (token) {
          error.config.headers['Authorization'] = `Bearer ${token}`;
          return axios(error.config);
        }
      }
      if (
        (error.response?.status === 400 &&
          ((typeof error.response?.data?.message === 'string' &&
            error.response.data.message === 'Credentials are invalid.') ||
            (typeof error.response?.data === 'string' &&
              error.response.data.includes('Credentials are invalid.')))) ||
        (error.response?.status === 401 &&
          (isAuthExpired(error.response?.data) || impersonating))
      ) {
        if (impersonating) {
          await revokeToken(true);
          localStorage.setItem('impersonationToken', '');
        } else {
          await revokeToken();
          localStorage.setItem('accessToken', '');
          localStorage.setItem('refreshToken', '');
          localStorage.setItem('impersonationToken', '');
        }
        window.location.reload();
        return;
      }

      // After the session logic, which matches on the API's original message.
      exposeValidationErrors(error);

      const originalRequest = error.config;

      if (!error.response) {
        return Promise.reject(error);
      }

      if (error.response.status === 401 && originalRequest._hasBeenRetried) {
        localStorage.setItem('accessToken', '');
        localStorage.setItem('refreshToken', '');
      }

      return Promise.reject(error);
    },
  );

  return instance;
};

const api = setupInterceptors(
  axios.create({ baseURL: `${API_NODE_BASE}/api/v1` }),
);

const internalApi = setupInterceptors(
  axios.create({ baseURL: `${API_NODE_BASE}/api/internal` }),
);

const apiV3 = setupInterceptors(
  axios.create({ baseURL: `${API_NODE_BASE}/api/v3` }),
);

const formatResponse = (response) => {
  if (!Boolean(response)) {
    return response;
  }

  return camelizeKeys(response);
};

// v3/internal list endpoints wrap results in { data, meta } — hoist the array
// back to response.data so consumers keep reading `response.data` as a list,
// and expose the pagination meta at response.meta.
export const unwrapListEnvelope = (response) => ({
  ...response,
  data: response?.data?.data ?? [],
  meta: response?.data?.meta,
});

export const getV3 = (uri, config = {}, formatResp = true) =>
  apiV3
    .get(uri, formatConfig(config))
    .then(formatResp ? formatResponse : (response) => response);

export const postV3 = (uri, payload = {}, config = {}) =>
  apiV3.post(uri, payload, formatConfig(config)).then(formatResponse);

export const get = (uri, config = {}, formatResp = true) =>
  api
    .get(uri, formatConfig(config))
    .then(formatResp ? formatResponse : (response) => response);

export const post = (uri, payload = {}, config, formatResp = true) =>
  api
    .post(uri, payload, formatConfig(config))
    .then(formatResp ? formatResponse : (response) => response);

export const put = (uri, payload = {}, config) =>
  api.put(uri, payload, formatConfig(config)).then(formatResponse);

export const patch = (uri, payload = {}, config) =>
  api.patch(uri, payload, formatConfig(config)).then(formatResponse);

export const destroy = (uri, payload = {}, config) =>
  api
    .delete(uri, {
      ...formatConfig(config),
      data: payload,
    })
    .then(formatResponse);

export const putFile = (uri, payload) =>
  api
    .put(uri, payload, { headers: { 'Content-Type': 'multipart/form-data' } })
    .then(formatResponse);

export const internalGet = (uri, config = {}, formatResp = true) =>
  internalApi
    .get(uri, formatConfig(config))
    .then(formatResp ? formatResponse : (response) => response);

export const internalPost = (uri, payload = {}, config, formatResp = true) =>
  internalApi
    .post(uri, payload, formatConfig(config))
    .then(formatResp ? formatResponse : (response) => response);

export const internalPut = (uri, payload = {}, config) =>
  internalApi.put(uri, payload, formatConfig(config)).then(formatResponse);

export const internalPatch = (uri, payload = {}, config) =>
  internalApi.patch(uri, payload, formatConfig(config)).then(formatResponse);

export const internalDestroy = (uri, payload = {}, config) =>
  internalApi
    .delete(uri, {
      ...formatConfig(config),
      data: payload,
    })
    .then(formatResponse);
